import type { Channel } from "@expert/shared-types";

export const CHANNEL_QUERY_PARAM = "channel";
export const CHANNEL_STORAGE_KEY = "expert-ui:channel";

const CHANNELS: readonly Channel[] = ["voice", "chat"];

function asChannel(value: string | null | undefined): Channel | undefined {
    return CHANNELS.find((channel) => channel === value);
}

function readStoredChannel(): Channel | undefined {
    try {
        return asChannel(globalThis.localStorage?.getItem(CHANNEL_STORAGE_KEY));
    } catch {
        return undefined;
    }
}

function storeChannel(channel: Channel) {
    try {
        globalThis.localStorage?.setItem(CHANNEL_STORAGE_KEY, channel);
    } catch {
        // Storage can be blocked; the channel still applies for this page load.
    }
}

/** `chat.expert.asurion.com`, `chat.npr.…`, and PR envs like `6054-chat.npr.…` serve the chat workspace. */
export function channelFromHostname(hostname: string): Channel | undefined {
    const [firstLabel = ""] = hostname.split(".");
    return firstLabel === "chat" || firstLabel.endsWith("-chat") ? "chat" : undefined;
}

/**
 * Resolves the workspace channel once per page load, so switching channels is a reload. Order:
 * `?channel=` (persisted, since the OIDC redirect drops query params), the persisted choice, the
 * hostname, then the build's `VITE_CHANNEL`.
 */
export function resolveChannel(buildChannel: Channel): Channel {
    const location = globalThis.location as Location | undefined;
    const fromQuery = asChannel(new URLSearchParams(location?.search).get(CHANNEL_QUERY_PARAM));
    if (fromQuery) {
        storeChannel(fromQuery);
        return fromQuery;
    }
    return readStoredChannel() ?? channelFromHostname(location?.hostname ?? "") ?? buildChannel;
}

/** Persists `channel` and reloads into it. Callers should ensure the expert has no active work first. */
export function switchChannel(channel: Channel) {
    storeChannel(channel);
    const url = new URL(globalThis.location.href);
    url.searchParams.set(CHANNEL_QUERY_PARAM, channel);
    globalThis.location.assign(url.toString());
}
